import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, m as motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { mfaService } from '@/services/mfa';
import { registerStepUpHandler } from '@/lib/stepUp';
import { useDialogA11y } from '@/lib/useDialogA11y';

/** Diálogo global de step-up (VLT-355): el Worker exige aal2 para llevarse
 *  bytes del vault cuando la cuenta tiene MFA. Aquí se supera el desafío TOTP
 *  sin salir de la pantalla y la descarga original se reintenta sola. */
export function StepUpDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  useEffect(() => {
    registerStepUpHandler(() => new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setCode('');
      setFailed(false);
      setOpen(true);
    }));
    return () => registerStepUpHandler(null);
  }, []);

  const close = (ok: boolean) => {
    setOpen(false);
    resolver.current?.(ok);
    resolver.current = null;
  };
  const dialogRef = useDialogA11y(open, () => close(false));

  const submit = async () => {
    if (code.trim().length < 6) return;
    setBusy(true);
    const ok = await mfaService.verifyChallenge(code.trim());
    setBusy(false);
    if (ok) close(true);
    else { setFailed(true); setCode(''); }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[90] grid place-items-center px-4" role="dialog" aria-modal="true" aria-label={t('stepUp.title')}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => close(false)} className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
          <motion.div ref={dialogRef}
            initial={{ opacity: 0, y: 14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 14, scale: 0.98 }}
            className="glass-strong relative w-full max-w-sm rounded-2xl p-6">
            <div className="mb-3 flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary"><ShieldCheck className="h-5 w-5" /></span>
              <h2 className="font-display font-semibold">{t('stepUp.title')}</h2>
            </div>
            <p className="mb-4 text-xs leading-relaxed text-muted-foreground">{t('stepUp.body')}</p>
            <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="space-y-3" noValidate>
              <input
                autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setFailed(false); }}
                placeholder="000000" aria-label={t('stepUp.codeLabel')}
                className="focus-ring w-full rounded-xl border border-border/60 bg-muted/30 px-3.5 py-2.5 text-center font-mono text-lg tracking-[0.4em] outline-none"
              />
              {failed && <p role="alert" className="text-xs text-danger">{t('stepUp.failed')}</p>}
              <div className="flex gap-2">
                <Button type="submit" size="sm" className="flex-1" loading={busy} disabled={code.length < 6}>
                  {t('stepUp.cta')}
                </Button>
                <Button type="button" size="sm" variant="glass" onClick={() => close(false)}>
                  {t('forms.cancel')}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
